import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/auth';
import { X, Calendar, TrendingUp } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ProspectingTabProps {
  clientId: string;
}

interface ProspectingSession {
  id: string;
  prospection_date: string;
  sdr_first_name: string;
  calls_count: number;
  conversations_count: number;
  rdvs_booked: number;
  session_summary: string | null;
  common_objections: string | null;
  market_feedback: string | null;
  improvement_suggestions: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_type: 'cold_calling' | 'mailing' | 'growth' | null;
}

const ProspectingTab: React.FC<ProspectingTabProps> = ({ clientId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [sessions, setSessions] = useState<ProspectingSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ProspectingSession | null>(null);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    setIsLoading(true);
    await fetchSessions();
    setIsLoading(false);
  };

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('prospecting_days')
        .select(`
          id,
          prospection_date,
          sdr_first_name,
          calls_count,
          conversations_count,
          rdvs_booked,
          session_summary,
          common_objections,
          market_feedback,
          improvement_suggestions,
          campaign_id,
          campaigns:campaign_id (
            name,
            type
          )
        `)
        .eq('client_id', clientId)
        .order('prospection_date', { ascending: false });

      if (error) throw error;

      const formattedSessions = (data || []).map((session: any) => ({
        id: session.id,
        prospection_date: session.prospection_date,
        sdr_first_name: session.sdr_first_name,
        calls_count: session.calls_count || 0,
        conversations_count: session.conversations_count || 0,
        rdvs_booked: session.rdvs_booked || 0,
        session_summary: session.session_summary,
        common_objections: session.common_objections,
        market_feedback: session.market_feedback,
        improvement_suggestions: session.improvement_suggestions,
        campaign_id: session.campaign_id,
        campaign_name: session.campaigns?.name || null,
        campaign_type: session.campaigns?.type || null,
      }));

      setSessions(formattedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const totalSessions = sessions.length;
  const totalCalls = sessions.reduce((sum, s) => sum + s.calls_count, 0);
  const totalRdvs = sessions.reduce((sum, s) => sum + s.rdvs_booked, 0);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getTypeBadge = (type: string | null) => {
    if (!type) return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">Non assignée</span>;

    switch (type) {
      case 'cold_calling':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">📞 Cold Calling</span>;
      case 'mailing':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">📧 Mailing</span>;
      case 'growth':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">🚀 Growth</span>;
      default:
        return null;
    }
  };

  const calculateRate = (rdvs: number, calls: number) => {
    if (calls === 0) return '0%';
    return `${Math.round((rdvs / calls) * 100)}%`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <Calendar className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Aucune session de prospection pour ce client
        </h3>
        <p className="text-gray-500 text-center max-w-md">
          Les sessions apparaîtront ici une fois la campagne démarrée
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Prospection</h2>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📆</span>
            <span className="text-sm font-medium text-gray-600">Total sessions</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalSessions}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📞</span>
            <span className="text-sm font-medium text-gray-600">Total appels</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalCalls}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🎯</span>
            <span className="text-sm font-medium text-gray-600">Total RDV</span>
          </div>
          <div className="text-3xl font-bold text-[#E91E8C]">{totalRdvs}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SDR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Appels
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RDV
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taux
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Détail
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">{formatDate(session.prospection_date)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{session.sdr_first_name}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{session.calls_count}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{session.conversations_count}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm ${session.rdvs_booked > 0 ? 'font-bold text-[#E91E8C]' : 'text-gray-900'}`}>
                      {session.rdvs_booked}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{calculateRate(session.rdvs_booked, session.calls_count)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => setSelectedSession(session)}
                      className="text-sm font-medium text-[#E91E8C] hover:text-[#d01a7b] transition-colors"
                    >
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSession && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSelectedSession(null)}
          />
          <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-gray-900 capitalize">
                      {formatDate(selectedSession.prospection_date)}
                    </h3>
                    {getTypeBadge(selectedSession.campaign_type)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium">{selectedSession.sdr_first_name}</span>
                    {selectedSession.campaign_name && (
                      <>
                        <span>•</span>
                        <span>{selectedSession.campaign_name}</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Appels</div>
                  <div className="text-xl font-bold text-gray-900">{selectedSession.calls_count}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Conversations</div>
                  <div className="text-xl font-bold text-gray-900">{selectedSession.conversations_count}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">RDV</div>
                  <div className="text-xl font-bold text-[#E91E8C]">{selectedSession.rdvs_booked}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Taux</div>
                  <div className="text-xl font-bold text-gray-900">
                    {calculateRate(selectedSession.rdvs_booked, selectedSession.calls_count)}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-8 py-6 space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">📝</span>
                  <h4 className="text-lg font-semibold text-gray-900">Résumé de session</h4>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selectedSession.session_summary || 'Aucun résumé'}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">🚫</span>
                  <h4 className="text-lg font-semibold text-gray-900">Objections fréquentes</h4>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selectedSession.common_objections || 'Aucune objection notée'}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">📊</span>
                  <h4 className="text-lg font-semibold text-gray-900">Retour marché</h4>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selectedSession.market_feedback || 'Aucun retour'}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">💡</span>
                  <h4 className="text-lg font-semibold text-gray-900">Axes d'amélioration</h4>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selectedSession.improvement_suggestions || 'Aucune suggestion'}
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-4">
              <button
                onClick={() => setSelectedSession(null)}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProspectingTab;
